/**
 * Galaxy wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const galaxyWallpapersPack: ToilePack = {
    slug: 'galaxy-wallpapers',
    category: 'screens',
    name: 'Galaxy wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'nebula', label: 'Nebula', subject: 'a purple and blue nebula of glowing gas clouds with bright stars scattered through it'},
        {key: 'spiral', label: 'Spiral galaxy', subject: 'a spiral galaxy seen at an angle, its bright core and arms of blue stars and dust lanes, on black'},
        {key: 'planet', label: 'Ringed planet', subject: 'a ringed planet rising over the grey horizon of a cratered moon, stars in the black sky above'},
    ],
    styles: [
        {key: 'hubble', label: 'Space photo', style: 'deep-space telescope image look, fine star field, glowing gas, natural cosmic color', palette: 'violet, indigo and magenta with white stars', framing: 'Full-bleed, the main object in the lower two thirds'},
        {key: 'paint', label: 'Cosmic paint', style: 'painterly digital art, swirled brushwork, glitter-like stars, a dreamy feel', palette: 'lilac, pink and deep blue on near black', framing: 'Full-bleed, the subject in the lower two thirds with deep sky above'},
        {key: 'minimal', label: 'Minimal space', style: 'minimal flat space illustration, smooth gradient sky, simple round shapes, a few clean stars', palette: 'dark navy to purple gradient with soft pink', framing: 'Full-bleed, the object small and low with empty sky above'},
    ],
    directions: ['black hole', 'astronaut', 'blue nebula'],
    directionHint: 'try: black hole',
    seo: {
        title: 'Galaxy Wallpapers: Nebula, Spiral, Ringed Planet',
        metaDescription: 'Galaxy phone wallpapers in purple and blue: a glowing nebula, a spiral galaxy and a ringed planet over a moon, in three styles.',
        h1: 'Galaxy wallpapers: a nebula, a spiral galaxy, a ringed planet',
        tagline: 'Nine phone wallpapers of deep sky in purple and blue, three objects in three styles.',
        intro: 'Phone wallpapers of deep space: a purple and blue nebula with bright stars, a spiral galaxy seen at an angle, and a ringed planet rising over a cratered moon. Each comes as a deep-space telescope image look, a painterly cosmic art and a minimal flat print. Space lovers can set them as they are. Type a different object, such as a black hole or an astronaut, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a gift for a space lover',
            'a science-room or planetarium print',
            'a dark desktop background',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three space scenes: a nebula with bright stars, a spiral galaxy, and a ringed planet over a moon. Each scene is shown in three styles.'},
            {q: 'Can I change the object?', a: 'Yes. Type a subject such as a black hole, an astronaut or a blue nebula; a typed subject is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Space photo has a fine star field and natural cosmic colour. Cosmic paint has swirled brushwork and glitter-like stars. Minimal space uses flat round shapes on a soft gradient sky.'},
            {q: 'Are these real telescope images?', a: 'No. The space photo style imitates a telescope image, but all nine pictures are rendered artworks, not astronomical observations.'},
        ],
    },
}
